import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Activity, CalendarClock, Lock, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PendingForm from "@/components/PendingForm";

function Shell({ children }) {
  return (
    <div className="app-bg min-h-screen flex items-center justify-center p-4" data-testid="action-page">
      <div className="w-full max-w-md glass glass-cyan rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <Activity className="w-5 h-5 text-cyan-400" />
          <span className="font-head font-black tracking-tight">Goodwin Living · Referral</span>
        </div>
        {children}
        <p className="mt-6 flex items-center gap-1.5 text-[11px] text-muted-foreground font-mono">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> This link performs one action only and never shows patient details.
        </p>
      </div>
    </div>
  );
}

function Handoff({ info, busy, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  if (!info.you_own) return null;
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="action-handoff-toggle"
            className="mt-4 w-full text-xs font-mono text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5">
      <Users className="w-3.5 h-3.5" /> Hand this off to someone else
    </button>
  );
  return (
    <div className="mt-4 space-y-2 rounded-lg border border-white/10 p-3" data-testid="action-handoff-block">
      <label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Hand off to</label>
      <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="action-handoff-select"
              className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
        <option value="">Select…</option>
        {info.handoff_options.map((c) => <option key={c.email} value={c.email}>{c.name}</option>)}
        <option value="__release">Release to the whole lane</option>
      </select>
      <Button onClick={() => onSubmit(to === "__release" ? "" : to)} disabled={busy || !to} data-testid="action-handoff-submit"
              variant="outline" className="w-full border-white/15 h-10">{busy ? "Saving…" : "Hand off"}</Button>
    </div>
  );
}

export default function ActionPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const preset = params.get("do");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(preset === "not_admitted");
  const [showPending, setShowPending] = useState(preset === "pending");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/actions/${token}`)
      .then((r) => { setInfo(r.data); setName(r.data.recipient_name || ""); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "This link is invalid."));
  }, [token]);

  const perform = async (action, reason_code, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/actions/${token}`, { action, reason_code, actor_name: name || undefined, ...extra });
      setDone({ action, status: data.status, followup: extra.followup_date, to: extra.to_email });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  if (err) return <Shell><div className="text-center py-4" data-testid="action-error">
    <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
    <p className="text-sm text-muted-foreground">{err}</p>
  </div></Shell>;

  if (!info) return <Shell><div className="flex items-center justify-center py-8 text-cyan-400">
    <Loader2 className="w-6 h-6 animate-spin" /></div></Shell>;

  if (done) return <Shell><div className="text-center py-4" data-testid="action-done">
    {done.action === "pending"
      ? <CalendarClock className="w-12 h-12 text-orange-400 mx-auto mb-3" />
      : <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />}
    <h2 className="font-head font-bold text-lg">
      {done.action === "handoff" ? (done.to ? "Handed off" : "Released to the lane") : done.action === "confirm" ? "Receipt confirmed" : done.action === "admit" ? "Admission recorded" : done.action === "pending" ? "Marked pending" : "Marked not admitted"}
    </h2>
    <p className="text-sm text-muted-foreground mt-1">
      {done.action === "pending"
        ? `We'll email you a reminder on ${fmtDate(done.followup)} to record the outcome.`
        : done.action === "handoff" ? (done.to ? "They've been emailed a one-tap link. You won't get further emails about this referral." : "Everyone in the lane has been emailed — the first to respond takes it over.")
        : done.action === "confirm" ? "You now own this referral — you'll get the one-tap link to record the outcome."
        : "Thank you — the coordinator has been updated automatically."}
    </p>
    <p className="text-xs font-mono text-muted-foreground mt-3">{info.referral_id} · {info.lane}</p>
  </div></Shell>;

  const RefBadge = () => (
    <div className="rounded-lg border border-white/10 bg-slate-900/40 px-4 py-3 mb-4" data-testid="action-ref">
      <div className="font-mono text-cyan-300 font-bold">{info.referral_id}</div>
      <div className="text-xs text-muted-foreground font-mono mt-0.5">patient {info.initials} · {info.lane}</div>
    </div>
  );

  const NameField = () => (
    <div className="mb-4">
      <label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Your name (optional)</label>
      <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="action-name"
             className="bg-slate-900/60 border-white/10 mt-1" placeholder="So we can log who acted" />
    </div>
  );

  if (info.blocked) return <Shell><RefBadge />
    <div className="text-center py-3" data-testid="action-blocked">
      <Lock className="w-9 h-9 text-amber-400 mx-auto mb-2" />
      <p className="text-sm">Being handled by <b data-testid="action-owner-name">{info.owner_name}</b>.</p>
      <p className="text-xs text-muted-foreground mt-1">Only one person updates a referral at a time. Nothing more to do here — they or a coordinator can hand it to you if needed.</p>
    </div></Shell>;

  // Confirm link
  if (info.kind === "confirm") {
    if (info.confirmed || info.used) return <Shell><RefBadge />
      <div className="text-center py-3" data-testid="action-already">
        <CheckCircle2 className="w-9 h-9 text-emerald-400 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">This referral is already confirmed. Nothing more to do here.</p>
      </div></Shell>;
    return <Shell><RefBadge />
      <p className="text-sm mb-4">A new referral has been sent to you. Please confirm you received it.{info.owner_name ? "" : " If others in your lane got this too, the first to confirm takes ownership."}</p>
      {NameField()}
      <Button onClick={() => perform("confirm")} disabled={busy} data-testid="action-confirm-btn"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold h-11">
        {busy ? "Confirming…" : "Confirm receipt"}
      </Button>
    </Shell>;
  }

  // Outcome link
  if (info.closed) return <Shell><RefBadge />
    <div className="text-center py-3" data-testid="action-already">
      <CheckCircle2 className="w-9 h-9 text-emerald-400 mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">The outcome for this referral has already been recorded.</p>
    </div></Shell>;

  return <Shell><RefBadge />
    {info.pending ? (
      <div className="flex items-start gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2 text-sm text-orange-300 mb-4" data-testid="action-pending-info">
        <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Pending on <b>{info.pending_reason || "—"}</b> · follow-up {fmtDate(info.followup_date)}. Record the outcome, or set a new follow-up date.</span>
      </div>
    ) : (
      <p className="text-sm mb-4">When there is an outcome, record it below. Still waiting on something? Mark it pending.</p>
    )}
    {NameField()}
    {showPending ? (
      <PendingForm reasons={info.pending_reasons} busy={busy} prefix="action" initialReason={info.pending_reason || ""}
                   submitLabel={info.pending ? "Save new follow-up" : "Mark pending"}
                   onSubmit={(b) => perform("pending", undefined, { pending_reason: b.pending_reason, followup_date: b.followup_date })}
                   onCancel={() => setShowPending(false)} />
    ) : !showReason ? (
      <div className="space-y-2">
        <Button onClick={() => perform("admit")} disabled={busy} data-testid="action-admit-btn"
                className={`w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 ${preset === "admit" ? "ring-2 ring-emerald-300 ring-offset-2 ring-offset-slate-950" : ""}`}>
          {busy ? "Saving…" : "Admit / Start of Care"}
        </Button>
        <Button onClick={() => setShowReason(true)} variant="outline" data-testid="action-notadmit-btn"
                className="w-full border-white/15 h-11">Not admitted</Button>
        <Button onClick={() => setShowPending(true)} variant="outline" data-testid="action-pending-btn"
                className="w-full border-orange-500/40 text-orange-300 h-11 gap-2">
          <CalendarClock className="w-4 h-4" /> {info.pending ? "Still pending · new date" : "Pending – follow-up needed"}
        </Button>
      </div>
    ) : (
      <div className="space-y-3" data-testid="action-reason-block">
        <div>
          <label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Reason not admitted</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="action-reason-select"
                  className="w-full h-11 mt-1 rounded-md bg-slate-900/60 border border-white/10 px-3 text-sm">
            <option value="">Select a reason…</option>
            {(info.reason_codes || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button onClick={() => perform("not_admitted", reason)} disabled={busy || !reason} data-testid="action-notadmit-submit"
                className="w-full bg-slate-200 hover:bg-white text-slate-950 font-semibold h-11">
          {busy ? "Saving…" : "Submit"}
        </Button>
        <button onClick={() => setShowReason(false)} className="w-full text-xs font-mono text-muted-foreground hover:text-foreground">back</button>
      </div>
    )}
    <Handoff info={info} busy={busy} onSubmit={(to) => perform("handoff", undefined, { to_email: to })} />
  </Shell>;
}
